import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Modal, ModalBody, ModalHeader, Spinner } from "@/Components/Common/Tailwind";
import { ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useUserStore } from "../../../store/store";
import { noticeService } from "../../../api/services/notice.service";
import moment from "moment";
import "moment/locale/ko";

moment.locale("ko");

const NoticeDetailSection = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permission] = useUserStore((state) => [state.permission]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  function togDeleteModal() {
    setDeleteModal(!deleteModal);
  }

  const deleteData = async () => {
    try {
      await noticeService.deleteNoticeById(id);
      navigate("/notice");
    } catch (error) {
      console.error(error);
      toast.error("삭제에 실패했습니다.");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await noticeService.getNotice(Number(id));
        setNotice(response.data);
      } catch (error) {
        console.error(error);
        toast.error("공지사항을 불러오는데 실패했습니다.");
        navigate("/notice");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return moment(dateStr).format("YYYY년 MM월 DD일");
  };

  const stickyHeader = (
    <header className="sticky top-0 z-50 flex items-center gap-1 border-b border-gray-100 bg-white/90 px-2 py-2.5 backdrop-blur-sm">
      <button
        type="button"
        onClick={() => navigate(-1)}
        aria-label="뒤로"
        className="flex h-10 w-10 items-center justify-center rounded-full text-gray-700 transition-[background-color,transform] hover:bg-gray-100 active:scale-[0.96]"
      >
        <ChevronLeft size={20} />
      </button>
      <h1 className="text-base font-bold text-gray-900">목록으로</h1>
    </header>
  );

  if (loading) {
    return (
      <React.Fragment>
        {stickyHeader}
        <div className="mt-4">
          <Container>
            <div className="text-center py-8">
              <Spinner color="primary" />
              <p className="mt-2">로딩 중...</p>
            </div>
          </Container>
        </div>
      </React.Fragment>
    );
  }

  if (!notice) {
    return (
      <React.Fragment>
        {stickyHeader}
        <div className="mt-4">
          <Container>
            <div className="text-center py-8">
              <p>공지사항을 찾을 수 없습니다.</p>
              <Button variant="primary" onClick={() => navigate("/notice")}>
                목록으로
              </Button>
            </div>
          </Container>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {stickyHeader}
      <div className="mt-6">
        <Container className="px-3 sm:px-4">
          <Row className="justify-center">
            <Col>
              <Card id="noticeDetail" className="bg-transparent shadow-none border-0">
                <CardHeader className="border-0 px-1 sm:px-2 pt-4 pb-3 bg-transparent">
                  <Row className="g-4 items-center">
                    <div className="col-sm">
                      <h1 className="mb-3 text-lg sm:text-2xl font-semibold text-gray-900 leading-snug">
                        <span
                          className={
                            notice.is_highlight
                              ? "box-decoration-clone rounded-sm bg-[#fde68a] px-1"
                              : undefined
                          }
                        >
                          {notice.title}
                        </span>
                      </h1>
                    </div>
                    {!!permission && permission.permission > 0 ? (
                      <div className="col-sm-3 ms-auto">
                        <div className="flex justify-content-sm-end gap-2">
                          <Button
                            className="btn-soft-success add-btn me-1"
                            onClick={() => navigate(`/notice/form/${id}`)}
                            id="edit-btn"
                          >
                            수정하기
                          </Button>
                          <Button
                            className="btn-soft-danger add-btn me-1"
                            onClick={togDeleteModal}
                            id="delete-btn"
                          >
                            삭제하기
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </Row>
                  <Row className="g-4 items-center">
                    <div className="col-sm">
                      <div className="text-sm text-gray-600">
                        <span>{`작성일 ${formatDate(notice.display_date || notice.created_at)}`}</span>
                      </div>
                    </div>
                  </Row>
                </CardHeader>

                <CardBody className="px-1 sm:px-2 pb-5 pt-3">
                  {notice.images && notice.images.length > 0 && (
                    <div className="mb-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {notice.images
                          .sort((a, b) => a.sort_order - b.sort_order)
                          .map((img, idx) => (
                            <div
                              key={img.id || idx}
                              className="cursor-pointer"
                              onClick={() => setSelectedImage(img.asset_url)}
                            >
                              <img
                                src={img.asset_url}
                                alt={`이미지 ${idx + 1}`}
                                className="w-full h-48 object-cover rounded-lg border border-gray-200 hover:opacity-90 transition-opacity"
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {notice.link_urls && notice.link_urls.length > 0 && (
                    <div className="mt-6">
                      <div className="flex flex-col gap-2">
                        {notice.link_urls.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-between px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors"
                          >
                            <span>{link.label || link.url}</span>
                            <i className="ri-external-link-line ms-2"></i>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <hr className="my-4" />

                  <div
                    className="prose prose-sm sm:prose-base max-w-none text-gray-800"
                    style={{ whiteSpace: "pre-wrap" }}
                    dangerouslySetInnerHTML={{ __html: notice.content }}
                  />

                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal
        id="deleteModal"
        isOpen={deleteModal}
        toggle={togDeleteModal}
      >
        <ModalHeader
          className="modal-title"
          id="deleteModalLabel"
          toggle={togDeleteModal}
        >
          삭제하기
        </ModalHeader>
        <ModalBody className="text-center p-5">
          <div className="mt-4">
            <h5 className="mb-3">해당 공지사항을 삭제하시겠습니까?</h5>
            <div className="flex flex-row items-center gap-2 justify-center">
              <Button
                type="button"
                variant="outline"
                className="bg-gradient-light fw-medium"
                onClick={togDeleteModal}
              >
                <i className="ri-close-line me-1 align-middle"></i>닫기
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => {
                  deleteData();
                  setDeleteModal(false);
                }}
              >
                삭제하기
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={!!selectedImage}
        toggle={() => setSelectedImage(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setSelectedImage(null)}>
          이미지 보기
        </ModalHeader>
        <ModalBody className="p-0">
          {selectedImage && (
            <img
              src={selectedImage}
              alt="상세 이미지"
              className="w-full"
            />
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default NoticeDetailSection;
